<template>
  <div class="wrap">
    <header class="top">
      <div>
        <b>Manager Portal</b>
        <small> — {{ user?.username }} ({{ user?.role }})</small>
      </div>
      <div class="actions">
        <button @click="refreshAll">Refresh</button>
        <button class="logout" @click="logout">Logout</button>
        <button @click="$router.push('/manager/flows')">Flows</button>
      </div>
    </header>

    <main class="grid">
      <!-- Agents -->
      <section class="card">
        <div class="cardHead">
          <h3>Agents</h3>
        </div>

        <div class="agents">
          <div v-for="a in agents" :key="a.id" class="agent">
            <div class="row">
              <b>{{ a.username }}</b>
              <span class="pill" :class="a.state">{{ a.state }}</span>
            </div>
            <small>Open chats: {{ a.open_chats }}</small>
          </div>
        </div>
      </section>

      <!-- Conversations -->
      <section class="card">
        <div class="cardHead">
          <h3>Conversations</h3>

          <div class="tabs">
            <button class="tab" :class="{on: filter==='OPEN'}" @click="setFilter('OPEN')">OPEN</button>
            <button class="tab" :class="{on: filter==='QUEUED'}" @click="setFilter('QUEUED')">QUEUED</button>
            <button class="tab" :class="{on: filter==='CLOSED'}" @click="setFilter('CLOSED')">CLOSED</button>
          </div>
        </div>

        <div class="convs">
          <div v-for="c in conversations" :key="c.conversation_id" class="conv">
            <div class="row">
              <b>#{{ c.conversation_id }}</b>
              <span class="pill" :class="c.status">{{ c.status }}</span>
              <span class="muted" style="margin-left:auto;">
                {{ c.assigned_username ? ("@" + c.assigned_username) : "Unassigned" }}
              </span>
            </div>

            <small>
              {{ c.visitor_name || "Visitor" }} • {{ c.visitor_phone || "" }}
            </small>

            <div class="preview" v-if="c.last_message">{{ c.last_message }}</div>

            <div class="row" style="margin-top:10px; gap:8px;">
              <select v-model="assignTo[c.conversation_id]" :disabled="filter==='CLOSED'">
                <option value="">Assign to…</option>
                <option v-for="a in agents" :key="a.id" :value="a.id">
                  {{ a.username }} ({{ a.state }})
                </option>
              </select>

              <button
                @click="assign(c.conversation_id)"
                :disabled="filter==='CLOSED' || !assignTo[c.conversation_id]"
              >
                Assign
              </button>

              <button
                class="danger"
                @click="closeConv(c.conversation_id)"
                :disabled="c.status==='CLOSED'"
              >
                Close
              </button>
            </div>
          </div>

          <div v-if="!conversations.length" class="muted" style="padding:10px;">
            No conversations found.
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { auth } from "@/services/auth";

const API_BASE = "https://sudarshan.fonetricks.com";

const router = useRouter();
const user = ref(auth.user);

const agents = ref([]);
const conversations = ref([]);
const filter = ref("OPEN");
const assignTo = ref({});

function logout() {
  auth.clear();
  router.push("/");
}

async function apiGet(path) {
  const r = await fetch(API_BASE + path, {
    headers: { Authorization: "Bearer " + auth.token },
  });
  if (r.status === 401) {
    logout();
    throw new Error("401");
  }
  const data = await r.json();
  if (!data.ok) throw new Error(data.message || "API_ERROR");
  return data;
}

async function apiPost(path, body) {
  const r = await fetch(API_BASE + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + auth.token,
    },
    body: JSON.stringify(body || {}),
  });
  if (r.status === 401) {
    logout();
    throw new Error("401");
  }
  const data = await r.json();
  if (!data.ok) throw new Error(data.message || "API_ERROR");
  return data;
}

async function loadAgents() {
  const data = await apiGet("/api/manager/agents");
  agents.value = data.agents || [];
}

async function loadConversations() {
  const data = await apiGet(`/api/manager/conversations?filter=${filter.value}`);
  conversations.value = data.conversations || [];
}

async function refreshAll() {
  await loadAgents();
  await loadConversations();
}

async function setFilter(v) {
  filter.value = v;
  await loadConversations();
}

async function assign(conversationId) {
  const agentId = assignTo.value[conversationId];
  if (!agentId) return;
  await apiPost(`/api/manager/conversation/${conversationId}/assign`, { agentId: Number(agentId) });
  await refreshAll();
}

async function closeConv(conversationId) {
  await apiPost(`/api/manager/conversation/${conversationId}/close`, {});
  await refreshAll();
}

onMounted(async () => {
  if (!auth.token) return router.push("/");
  if (!["admin", "manager"].includes(auth.user?.role)) return router.push("/inbox");
  await refreshAll();
});
</script>

<style scoped>
.wrap { font-family: Arial; }
.top {
  display:flex; justify-content:space-between; align-items:center;
  padding: 12px 16px; border-bottom: 1px solid #eee;
}
.actions { display:flex; gap:8px; }
button { padding: 8px 10px; border: none; border-radius: 10px; background:#0b66d1; color:#fff; cursor:pointer; }
button.logout { background:#444; }
button.danger { background:#e74c3c; }

.grid { display:grid; grid-template-columns: 360px 1fr; gap: 12px; padding: 12px; }
.card { border:1px solid #eee; border-radius: 12px; overflow:hidden; }
.cardHead { padding: 10px 12px; border-bottom: 1px solid #eee; display:flex; justify-content:space-between; align-items:center; gap:12px; }
h3 { margin:0; }

.agents { padding: 12px; display:flex; flex-direction:column; gap:10px; max-height: calc(100vh - 110px); overflow:auto; }
.agent { padding: 10px; border:1px solid #eee; border-radius: 12px; background:#fafafa; }
.row { display:flex; align-items:center; gap:8px; }

.tabs { display:flex; gap:8px; }
.tab { background:#eee; color:#000; }
.tab.on { background:#0b66d1; color:#fff; }

.convs { padding: 12px; display:flex; flex-direction:column; gap:10px; max-height: calc(100vh - 110px); overflow:auto; }
.conv { padding: 10px; border:1px solid #eee; border-radius: 12px; }
.preview { margin-top:6px; font-size: 12px; color:#555; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

select { padding: 8px; border-radius: 10px; border:1px solid #ccc; }
.muted { color:#777; font-size: 12px; }

.pill { padding: 2px 8px; border-radius: 999px; border:1px solid #ddd; font-size: 11px; background:#f5f5f5; }
.pill.READY { background:#e9f6ee; border-color:#bfe6cc; }
.pill.AWAY { background:#fff5e6; border-color:#ffe0a3; }
.pill.OFFLINE { background:#f5f5f5; border-color:#ddd; }
.pill.OPEN { background:#e9f6ee; border-color:#bfe6cc; }
.pill.CLOSED { background:#f5f5f5; border-color:#ddd; }
</style>